/**
 * Splice virtual stations referenced by `workAssignmentSequences` into
 * the `stations[]` array so downstream placement math has an index for
 * every id the sequences point at.
 *
 * A "virtual" station is one that appears as `fromStation` or `toStation`
 * on a sequence but is not in `stations[]` (backend does not include
 * mid-route breakdown locations in `stations`, only in the sequences).
 *
 * The virtual station is anchored between the two real stations the pair
 * of surrounding sequences connect: the sequence *ending* at the virtual
 * feeds `arrival`, the sequence *leaving* it feeds `departure`.
 *
 * @typedef {Object} RideStation
 * @property {string} id
 * @property {string} code
 * @property {string} [name]
 * @property {string} [timezone]
 * @property {string | null} [departure]
 * @property {string | null} [arrival]
 * @property {Object | null} [city]
 * @property {boolean} [isVirtual]
 * @property {Object} [raw]
 * @property {string} __typename
 *
 * @typedef {Object} SequenceStationRef
 * @property {string} id
 * @property {string} [name]
 *
 * @typedef {Object} WorkAssignmentSequenceLike
 * @property {SequenceStationRef} fromStation
 * @property {SequenceStationRef} toStation
 * @property {string} [fromTimestamp]
 * @property {string} [toTimestamp]
 *
 * @param {{
 *   stations: RideStation[],
 *   workAssignmentSequences: WorkAssignmentSequenceLike[],
 * }} input
 * @returns {RideStation[]}
 */
/**
 * Upsert a virtual-station entry into `entries` using role-specific fields.
 * Called once per role (inbound / outbound); the caller passes the timestamp
 * field, the anchor field, and the anchor value for that role.
 *
 * @param {{
 *   entries: Map<string, Object>,
 *   virtualId: string,
 *   rawStation: Object,
 *   timestampField: 'arrival' | 'departure',
 *   timestampValue: string | null | undefined,
 *   anchorField: 'anchorBeforeId' | 'anchorAfterId',
 *   anchorValue: string | undefined,
 * }} args
 */
const upsertVirtualEntry = ({
	entries,
	virtualId,
	rawStation,
	timestampField,
	timestampValue,
	anchorField,
	anchorValue,
}) => {
	const entry = entries.get(virtualId) ?? {};
	entry.id = virtualId;
	entry.raw = entry.raw ?? rawStation;
	entry[timestampField] = timestampValue ?? null;
	entry[anchorField] = entry[anchorField] ?? anchorValue;
	entries.set(virtualId, entry);
};

/**
 * Pick the physical station that sits directly next to `virtualId` in the
 * route. When the sequence spans multiple physical stations, the truthful
 * neighbour lives inside `workAssignments[]` — the segment that ends at
 * (or starts from) the virtual station. Fall back to the sequence-level
 * endpoint when segments are missing so single-segment sequences keep the
 * ROR-1902 behaviour unchanged.
 *
 * @param {{
 *   sequence: WorkAssignmentSequenceLike,
 *   virtualId: string,
 *   side: 'to' | 'from',
 * }} args
 * @returns {string | undefined}
 */
const pickAnchorForVirtual = ({ sequence, virtualId, side }) => {
	const segments = Array.isArray(sequence.workAssignments) ? sequence.workAssignments : [];
	const segment = segments.find((wa) =>
		side === 'to' ? wa?.toStation?.id === virtualId : wa?.fromStation?.id === virtualId
	);
	if (segment) {
		return side === 'to' ? segment.fromStation?.id : segment.toStation?.id;
	}
	return side === 'to' ? sequence.fromStation?.id : sequence.toStation?.id;
};

export const insertVirtualStations = ({ stations, workAssignmentSequences }) => {
	const physicalIds = new Set(stations.map((station) => station.id));

	// Collect the pair of sequences around each virtual id:
	// - the one landing at it feeds `arrival` (its `toTimestamp`);
	// - the one leaving it feeds `departure` (its `fromTimestamp`).
	// `raw` is the original station reference from the response, kept for
	// debugging and future breakdown UI.
	const virtualEntries = new Map();
	workAssignmentSequences.forEach((sequence) => {
		const fromId = sequence.fromStation?.id;
		const toId = sequence.toStation?.id;
		const fromIsVirtual = fromId && !physicalIds.has(fromId);
		const toIsVirtual = toId && !physicalIds.has(toId);

		if (toIsVirtual && !fromIsVirtual) {
			upsertVirtualEntry({
				entries: virtualEntries,
				virtualId: toId,
				rawStation: sequence.toStation,
				timestampField: 'arrival',
				timestampValue: sequence.toTimestamp,
				anchorField: 'anchorBeforeId',
				anchorValue: pickAnchorForVirtual({ sequence, virtualId: toId, side: 'to' }),
			});
		} else if (fromIsVirtual && !toIsVirtual) {
			upsertVirtualEntry({
				entries: virtualEntries,
				virtualId: fromId,
				rawStation: sequence.fromStation,
				timestampField: 'departure',
				timestampValue: sequence.fromTimestamp,
				anchorField: 'anchorAfterId',
				anchorValue: pickAnchorForVirtual({ sequence, virtualId: fromId, side: 'from' }),
			});
		}
	});

	const enriched = [...stations];
	virtualEntries.forEach((entry) => {
		const anchorIndex = entry.anchorBeforeId
			? enriched.findIndex((station) => station.id === entry.anchorBeforeId)
			: enriched.findIndex((station) => station.id === entry.anchorAfterId);
		if (anchorIndex === -1) return;
		const insertAt = entry.anchorBeforeId ? anchorIndex + 1 : anchorIndex;
		enriched.splice(insertAt, 0, {
			id: entry.id,
			code: entry.raw?.code ?? null,
			city: null,
			arrival: entry.arrival ?? null,
			departure: entry.departure ?? null,
			isVirtual: true,
			raw: entry.raw,
			__typename: 'RideStation',
		});
	});

	return enriched;
};
